import { usePreferences } from "@contexts/PreferenceContext";
import {
  usePermissions,
  useTenants,
  useWhoAmI,
  type Tenant,
} from "@repo/components/ctrl/hooks";
import { toast } from "@repo/ui/na";
import { useMemo } from "react";

export type Role = "developer" | "tenantAdmin" | "globalAdmin";

export const ROLE_LABEL: Record<Role, string> = {
  developer: "Developer",
  tenantAdmin: "Tenant admin",
  globalAdmin: "Global admin",
};

/**
 * The signed-in viewer: identity, role in the active tenant, and the
 * tenants they can switch between. Role is server-driven
 * (`/permissions/me`), never derived from JWT claims.
 */
export function useSession() {
  const { preferences, setPreference } = usePreferences();
  const { data: whoami, isLoading: whoLoading } = useWhoAmI();
  const { permissions, isGlobalAdmin, isLoading: permLoading } = usePermissions();
  const { data: allTenants } = useTenants({ shouldRetryOnError: false });

  return useMemo(() => {
    const permitted = permissions?.tenants ?? [];
    const tenants: (Tenant & { manage: boolean })[] = (
      isGlobalAdmin
        ? (allTenants ?? [])
        : permitted.map(
            (p) => allTenants?.find((t) => t.id === p.id) ?? { id: p.id, name: p.id },
          )
    ).map((t) => ({
      ...t,
      manage:
        isGlobalAdmin ||
        Boolean(permitted.find((p) => p.id === t.id)?.relations?.includes("can_manage")),
    }));

    const tenantId =
      (preferences?.tenant && tenants.some((t) => t.id === preferences.tenant)
        ? preferences.tenant
        : undefined) ??
      whoami?.tenant_id ??
      tenants[0]?.id;
    const tenant = tenants.find((t) => t.id === tenantId);

    const role: Role = isGlobalAdmin
      ? "globalAdmin"
      : tenant?.manage
        ? "tenantAdmin"
        : "developer";

    return {
      email: whoami?.email,
      userId: whoami?.sub,
      groups: whoami?.groups ?? [],
      role,
      isAdmin: role !== "developer",
      isGlobalAdmin,
      tenantId,
      tenant,
      tenants,
      /** Switch the active tenant; toasts only when it actually changes. */
      setTenant: (id: string) => {
        if (id === tenantId) return;
        setPreference?.("tenant", id);
        const t = tenants.find((x) => x.id === id);
        const slug = (t as { slug?: string } | undefined)?.slug ?? id;
        toast("Switched tenant", `${t?.name ?? id} · realm ${slug}`);
      },
      isLoading: whoLoading || permLoading,
    };
  }, [whoami, permissions, isGlobalAdmin, allTenants, preferences?.tenant, setPreference, whoLoading, permLoading]);
}

export type Session = ReturnType<typeof useSession>;
